import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { ChangeSearchCriteriaByListType, SearchCriteriaInitialStateByListType } from './list-search-criteria.actions';
import { TruckDriversSearchCriteria, UserListSearchCriteria } from '../administration/user/models';
import { PartnerListSearchCriteria, PartnerSearchCriteria } from '../administration/partner/models';
import { ApiConnectionListSearchCriteria } from '../administration/api-connection/models';
import { NewsletterListSearchCriteria } from '../administration/newsletter/models/newsletter.model';
import { PlannerProfileListSearchCriteria } from '../../models/planner-profile.model';
import { DeliveryNoteHandlingUnitSearchCriteria } from '../delivery-note/models/delivery-note.model';
import {
  HubListSearchCriteria,
  HubLoanerPhoneListSearchCriteria,
  YardSpotSearchCriteria,
} from '../administration/hub/models';
import {
  CompartmentListSearchCriteria,
  CompartmentAssignedUserListSearchCriteria,
  CompartmentRuleListSearchCriteria,
} from '../administration/compartment/models';
import { DockListSearchCriteria } from '../administration/dock/models';
import { HubFrequentDriverListSearchCriteria } from '../administration/hub/models/hub-frequent-driver-list.model';
import { ClosedPeriodListSearchCriteria } from '../administration/hub/closed-period/models/closed-periods.model';
import { ListSearchCriteriaType } from './constants/list-search-criteria-type.constant';
import { AllocationViewProfileListSearchCriteria } from '../monitoring/allocation-view-profiles/models/';
import { MasterDataListSearchCriteria } from '../report/master-data';
import { AddressListSearchCriteria } from '../address';
import { Sc3FailedMessagesListSearchCriteria, Sc4FailedMessagesListSearchCriteria } from '../data-quality';
import { ShipmentSearchCriteria } from '../monitoring/shipment/models/shipment.model';
import { EulaListSearchCriteria, PlantListSearchCriteria } from '../administration';
import { ExportsListSearchCriteria } from '../exports';
import { FailedMessagesProfileListSearchCriteria } from '../data-quality/failed-messages/models/profile/failed-messages-profile-search-criteria.model';
import { UserDeletionHistoryListSearchCriteria } from '../report';
import {
  ShipmentWaybillGoodsItemListSearchCriteria,
  ShipmentWaybillMaterialListSearchCriteria,
} from '../shipment-waybill';
import { CouplingPlanSearchCriteria } from '../monitoring/allocation-view/models/allocation-register-uli-details/trailer-handling/register-uli-trailer-handling.model';

export type ListSearchCriteriaTypes =
  | UserListSearchCriteria
  | PartnerListSearchCriteria
  | HubListSearchCriteria
  | CompartmentListSearchCriteria
  | CompartmentAssignedUserListSearchCriteria
  | DockListSearchCriteria
  | EulaListSearchCriteria
  | ApiConnectionListSearchCriteria
  | NewsletterListSearchCriteria
  | DeliveryNoteHandlingUnitSearchCriteria
  | PlannerProfileListSearchCriteria
  | HubLoanerPhoneListSearchCriteria
  | HubFrequentDriverListSearchCriteria
  | ClosedPeriodListSearchCriteria
  | ShipmentWaybillGoodsItemListSearchCriteria
  | ShipmentWaybillMaterialListSearchCriteria
  | AllocationViewProfileListSearchCriteria
  | ExportsListSearchCriteria
  | CompartmentRuleListSearchCriteria
  | MasterDataListSearchCriteria
  | Sc4FailedMessagesListSearchCriteria
  | Sc3FailedMessagesListSearchCriteria
  | AddressListSearchCriteria
  | ShipmentSearchCriteria
  | PartnerSearchCriteria
  | UserDeletionHistoryListSearchCriteria
  | CouplingPlanSearchCriteria
  | TruckDriversSearchCriteria
  | YardSpotSearchCriteria;

export interface ListSearchCriteriaStateModel {
  [ListSearchCriteriaType.Users]: UserListSearchCriteria;
  [ListSearchCriteriaType.Partners]: PartnerListSearchCriteria;
  [ListSearchCriteriaType.Eulas]: EulaListSearchCriteria;
  [ListSearchCriteriaType.ApiConnections]: ApiConnectionListSearchCriteria;
  [ListSearchCriteriaType.Newsletters]: NewsletterListSearchCriteria;
  [ListSearchCriteriaType.TransportPlannerProfiles]: PlannerProfileListSearchCriteria;
  [ListSearchCriteriaType.TransportManagementProfiles]: PlannerProfileListSearchCriteria;
  [ListSearchCriteriaType.AutomotiveAftermarketProfiles]: PlannerProfileListSearchCriteria;
  [ListSearchCriteriaType.AllocationViewProfiles]: AllocationViewProfileListSearchCriteria;
  [ListSearchCriteriaType.HandlingUnitIds]: DeliveryNoteHandlingUnitSearchCriteria;
  [ListSearchCriteriaType.Hubs]: HubListSearchCriteria;
  [ListSearchCriteriaType.Compartments]: CompartmentListSearchCriteria;
  [ListSearchCriteriaType.CompartmentAssignedUsers]: CompartmentAssignedUserListSearchCriteria;
  [ListSearchCriteriaType.CompartmentRules]: CompartmentRuleListSearchCriteria;
  [ListSearchCriteriaType.LoanerPhones]: HubLoanerPhoneListSearchCriteria;
  [ListSearchCriteriaType.DriverList]: HubFrequentDriverListSearchCriteria;
  [ListSearchCriteriaType.Docks]: DockListSearchCriteria;
  [ListSearchCriteriaType.ClosedPeriods]: ClosedPeriodListSearchCriteria;
  [ListSearchCriteriaType.GoodsItems]: ShipmentWaybillGoodsItemListSearchCriteria;
  [ListSearchCriteriaType.WaybillMaterials]: ShipmentWaybillMaterialListSearchCriteria;
  [ListSearchCriteriaType.Exports]: ExportsListSearchCriteria;
  [ListSearchCriteriaType.AddressManagement]: AddressListSearchCriteria;
  [ListSearchCriteriaType.MasterDataReport]: MasterDataListSearchCriteria;
  [ListSearchCriteriaType.Sc4FailedMessages]: Sc4FailedMessagesListSearchCriteria;
  [ListSearchCriteriaType.Sc3FailedMessages]: Sc3FailedMessagesListSearchCriteria;
  [ListSearchCriteriaType.Shipment]: ShipmentSearchCriteria;
  [ListSearchCriteriaType.ShipmentPlannerProfiles]: PlannerProfileListSearchCriteria;
  [ListSearchCriteriaType.FailedMessagesProfiles]: FailedMessagesProfileListSearchCriteria;
  [ListSearchCriteriaType.PartnersList]: PartnerSearchCriteria;
  [ListSearchCriteriaType.PartnersList]: PartnerSearchCriteria;
  [ListSearchCriteriaType.Plants]: PlantListSearchCriteria;
  [ListSearchCriteriaType.DeletionHistory]: UserDeletionHistoryListSearchCriteria;
  [ListSearchCriteriaType.TruckDrivers]: TruckDriversSearchCriteria;
  [ListSearchCriteriaType.CouplingPlan]: CouplingPlanSearchCriteria;
  [ListSearchCriteriaType.YardSpots]: YardSpotSearchCriteria;
}

const initialState: ListSearchCriteriaStateModel = {
  [ListSearchCriteriaType.Users]: new UserListSearchCriteria(),
  [ListSearchCriteriaType.Partners]: new PartnerListSearchCriteria(),
  [ListSearchCriteriaType.Eulas]: new EulaListSearchCriteria(),
  [ListSearchCriteriaType.ApiConnections]: new ApiConnectionListSearchCriteria(),
  [ListSearchCriteriaType.Newsletters]: new NewsletterListSearchCriteria(),
  [ListSearchCriteriaType.TransportPlannerProfiles]: new PlannerProfileListSearchCriteria(),
  [ListSearchCriteriaType.TransportManagementProfiles]: new PlannerProfileListSearchCriteria(),
  [ListSearchCriteriaType.AutomotiveAftermarketProfiles]: new PlannerProfileListSearchCriteria(),
  [ListSearchCriteriaType.AllocationViewProfiles]: new AllocationViewProfileListSearchCriteria(),
  [ListSearchCriteriaType.HandlingUnitIds]: new DeliveryNoteHandlingUnitSearchCriteria(),
  [ListSearchCriteriaType.Hubs]: new HubListSearchCriteria(),
  [ListSearchCriteriaType.Compartments]: new CompartmentListSearchCriteria(),
  [ListSearchCriteriaType.CompartmentAssignedUsers]: new CompartmentAssignedUserListSearchCriteria(),
  [ListSearchCriteriaType.CompartmentRules]: new CompartmentRuleListSearchCriteria(),
  [ListSearchCriteriaType.Docks]: new DockListSearchCriteria(),
  [ListSearchCriteriaType.LoanerPhones]: new HubLoanerPhoneListSearchCriteria(),
  [ListSearchCriteriaType.DriverList]: new HubFrequentDriverListSearchCriteria(),
  [ListSearchCriteriaType.ClosedPeriods]: new ClosedPeriodListSearchCriteria(),
  [ListSearchCriteriaType.GoodsItems]: new ShipmentWaybillGoodsItemListSearchCriteria(),
  [ListSearchCriteriaType.WaybillMaterials]: new ShipmentWaybillMaterialListSearchCriteria(),
  [ListSearchCriteriaType.Exports]: new ExportsListSearchCriteria(),
  [ListSearchCriteriaType.AddressManagement]: new AddressListSearchCriteria(),
  [ListSearchCriteriaType.MasterDataReport]: new MasterDataListSearchCriteria(),
  [ListSearchCriteriaType.Sc4FailedMessages]: new Sc4FailedMessagesListSearchCriteria(),
  [ListSearchCriteriaType.Sc3FailedMessages]: new Sc3FailedMessagesListSearchCriteria(),
  [ListSearchCriteriaType.Shipment]: new ShipmentSearchCriteria(),
  [ListSearchCriteriaType.ShipmentPlannerProfiles]: new PlannerProfileListSearchCriteria(),
  [ListSearchCriteriaType.FailedMessagesProfiles]: new FailedMessagesProfileListSearchCriteria(),
  [ListSearchCriteriaType.PartnersList]: new PartnerSearchCriteria(),
  [ListSearchCriteriaType.Plants]: new PlantListSearchCriteria(),
  [ListSearchCriteriaType.DeletionHistory]: new UserDeletionHistoryListSearchCriteria(),
  [ListSearchCriteriaType.TruckDrivers]: new TruckDriversSearchCriteria(),
  [ListSearchCriteriaType.CouplingPlan]: new CouplingPlanSearchCriteria(),
  [ListSearchCriteriaType.YardSpots]: new YardSpotSearchCriteria(),
};

@State<ListSearchCriteriaStateModel>({
  name: 'listSearchCriteria',
  defaults: initialState,
})
@Injectable()
export class ListSearchCriteriaState {
  @Selector()
  static getState(state: ListSearchCriteriaStateModel): ListSearchCriteriaStateModel {
    return state;
  }

  @Selector()
  static getSearchCriteriaByListType(
    state: ListSearchCriteriaStateModel,
  ): (type: ListSearchCriteriaType) => ListSearchCriteriaTypes {
    return (type: ListSearchCriteriaType) => state[type];
  }

  @Action(ChangeSearchCriteriaByListType)
  changeSearchCriteriaByListType(
    ctx: StateContext<ListSearchCriteriaStateModel>,
    { payload: { searchCriteria, listSearchCriteriaType } }: ChangeSearchCriteriaByListType,
  ): void {
    ctx.patchState({
      [listSearchCriteriaType]: {
        ...ctx.getState()[listSearchCriteriaType],
        ...searchCriteria,
      },
    });
  }

  @Action(SearchCriteriaInitialStateByListType)
  setInitialStateByListType(
    ctx: StateContext<ListSearchCriteriaStateModel>,
    { payload }: SearchCriteriaInitialStateByListType,
  ): void {
    ctx.patchState({
      [payload]: {
        ...initialState[payload],
      },
    });
  }
}
